import { AVAILABLE_TOOLS, TOOL_CATEGORIES } from '../_constants/toolRegistry';
import { en } from '../i18n/_en';

export type ToolId = (typeof AVAILABLE_TOOLS)[number]['id'];

function humanizeSegment(segment: string): string {
  const spaced = segment.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return spaced
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatToolId(toolId: string): string {
  return toolId
    .split('-')
    .map((seg) => humanizeSegment(seg))
    .join(' ');
}

function formatCategory(category: string): string {
  return humanizeSegment(category);
}

/**
 * Get the localized display name for a tool
 */
export function getToolDisplayName(
  toolId: string,
  t: typeof en,
): string | undefined {
  const names = t.aiManagement.tools.names as Record<
    string,
    string | undefined
  >;
  const translated = names[toolId];
  return translated ?? formatToolId(toolId);
}

/**
 * Get the localized description for a tool
 * Currently tools don't have separate descriptions, so return undefined
 */
export function getToolDescription(
  toolId: string,
  t: typeof en,
): string | undefined {
  const key = toolId as keyof typeof en.aiManagement.tools.descriptions;
  return t.aiManagement.tools.descriptions[key];
}

/**
 * Get the category for a tool
 */
export function getToolCategory(
  toolId: string,
  t: typeof en,
): string | undefined {
  const tool = AVAILABLE_TOOLS.find((t) => t.id === toolId);
  if (!tool) return undefined;

  const categoryKey =
    tool.category as keyof typeof en.aiManagement.tools.categories;
  return (
    t.aiManagement.tools.categories[categoryKey] ??
    formatCategory(tool.category)
  );
}

/**
 * Get all available tool IDs
 */
export function getAllToolIds(): string[] {
  return AVAILABLE_TOOLS.map((tool) => tool.id);
}

/**
 * Check if a tool ID is valid
 */
export function isValidToolId(toolId: string): boolean {
  return AVAILABLE_TOOLS.some((tool) => tool.id === toolId);
}

/**
 * Get all tool categories
 */
export function getAllCategories(): readonly string[] {
  return TOOL_CATEGORIES;
}
